// components/phones/HorizontalPanStage.jsx
import React, { useEffect, useRef, useState } from "react";

/**
 * HorizontalPanStage (responsive fixed-panel sizes)
 *
 * Props:
 * - images: [{src, alt}]
 * - sizeMap: optional function (vw, vh) => ({ panelWidth, panelHeight })
 *            If not provided, a default map (breakpoints -> sizes) is used.
 * - spacerExtra: px breathing room
 * - navHeight: px height of fixed header
 * - slides: override number of logical panels (default images.length)
 *
 * Behavior:
 * - Uses fixed pixel panel sizes determined from viewport via sizeMap.
 * - Each panel container uses panelWidth x panelHeight and images are centered / contain.
 * - Horizontal distance = (slides - 1) * panelWidth.
 * - No stacked mobile fallback (pan always active). If you want stacked on very small screens, add condition.
 */
export default function HorizontalPanStage({
    images = [],
    sizeMap = null,
    spacerExtra = 120,
    navHeight = 0,
    slides = null,
}) {
    const rootRef = useRef(null);
    const trackRef = useRef(null);
    const spacerRef = useRef(null);
    const rafRef = useRef(null);

    const [panelSize, setPanelSize] = useState({ panelWidth: 890, panelHeight: 895 });
    const [metrics, setMetrics] = useState({
        vw: 0,
        vh: 0,
        slidesCount: slides || images.length,
        horizontalDistance: 0,
        spacerHeight: 0,
        progress: 0,
    });
    const [ready, setReady] = useState(false);

    // Default sizeMap: choose panel width/height per viewport width buckets.
    // Edit these buckets to match your design. Values are px.
    const defaultSizeMap = (vw, vh) => {

        if (vw >= 820) return { panelWidth: 890, panelHeight: 895 }; // your original
        if (vw >= 640) return { panelWidth: 760, panelHeight: Math.max(420, vh - navHeight - 80) };
        // for narrow phones, still keep pan but much smaller
        return { panelWidth: Math.max(420, Math.round(vw * 0.9)), panelHeight: Math.max(380, Math.round(vh - navHeight - 40)) };
    };

    // helper: preload images to avoid jumps
    const preload = async () =>
        Promise.all(
            images.map(
                (it) =>
                    new Promise((res) => {
                        const img = new Image();
                        img.src = it.src;
                        if (img.complete) return res();
                        img.onload = () => res();
                        img.onerror = () => res();
                    })
            )
        );

    // compute panel size, metrics, and set spacer height
    const compute = async () => {
        await preload();
        const vw = window.innerWidth;
        const vh = window.innerHeight;

        const mapper = typeof sizeMap === "function" ? sizeMap : defaultSizeMap;
        const { panelWidth, panelHeight } = mapper(vw, vh);

        // compute slides
        const slidesCount = Math.max(1, slides || images.length);

        // horizontal distance = (slides - 1) * panelWidth
        const horizontalDistance = Math.max(0, (slidesCount - 1) * panelWidth);

        // visible stage height is panelHeight (we place the sticky stage to match panelHeight)
        const visibleStageHeight = panelHeight;

        // spacer: vertical scroll area that maps to the horizontal pan
        const spacerHeight = Math.ceil(horizontalDistance + visibleStageHeight + spacerExtra);

        // apply spacer / track size
        if (spacerRef.current) spacerRef.current.style.height = `${spacerHeight}px`;
        if (trackRef.current) trackRef.current.style.height = `${visibleStageHeight}px`;

        setPanelSize({ panelWidth, panelHeight });
        setMetrics({
            vw,
            vh,
            slidesCount,
            horizontalDistance,
            spacerHeight,
            progress: 0,
        });
        setReady(true);
    };

    // init + resize
    useEffect(() => {
        compute();
        let t;
        const onResize = () => {
            clearTimeout(t);
            t = setTimeout(() => compute(), 80);
        };
        window.addEventListener("resize", onResize);
        return () => {
            clearTimeout(t);
            window.removeEventListener("resize", onResize);
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [images, sizeMap, spacerExtra, navHeight, slides]);

    // scroll -> translate track
    useEffect(() => {
        if (!ready) return;

        const update = () => {
            rafRef.current = null;
            if (!rootRef.current || !trackRef.current || !spacerRef.current) return;

            const { horizontalDistance, spacerHeight } = metrics;
            const visibleStageHeight = panelSize.panelHeight;

            const rootTop = rootRef.current.offsetTop; // top of the root section
            const startY = Math.max(0, rootTop - navHeight);

            const maxOffset = Math.max(0, spacerHeight - visibleStageHeight);
            const y = window.scrollY;
            let offset;
            if (y <= startY) offset = 0;
            else offset = Math.min(maxOffset, y - startY);

            const progress = maxOffset === 0 ? 1 : offset / maxOffset;
            const translateX = -progress * horizontalDistance;

            trackRef.current.style.transform = `translate3d(${translateX}px, 0, 0)`;
            setMetrics((m) => ({ ...m, progress }));
        };

        const onScroll = () => {
            if (rafRef.current) return;
            rafRef.current = requestAnimationFrame(update);
        };

        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => {
            window.removeEventListener("scroll", onScroll);
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        };
    }, [ready, metrics, navHeight, panelSize]);

    // Render
    const { panelWidth, panelHeight } = panelSize;
    const { spacerHeight } = metrics;

    return (
        <section ref={rootRef} className="w-full relative">
            {/* Sticky stage matching panelHeight */}
            <div
                className="sticky left-0 right-0 overflow-hidden"
                style={{
                    top: `${navHeight}px`,
                    height: `${panelHeight}px`,
                    zIndex: 20,
                    background: "#DCCFFB",
                }}
            >
                <div
                    ref={trackRef}
                    className="flex will-change-transform"
                    style={{
                        height: `${panelHeight}px`,
                        transform: "translate3d(0,0,0)",
                    }}
                >
                    {images.map((img, idx) => (
                        <div
                            key={idx}
                            style={{
                                width: `${panelWidth}px`,
                                height: `${panelHeight}px`,
                                flex: "0 0 auto",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                overflow: "hidden",
                                boxSizing: "border-box",
                                // optional: add a little right padding to visually separate panels if needed
                                // paddingRight: "0px",
                            }}
                        >
                            <img
                                src={img.src}
                                alt={img.alt || ""}
                                draggable={false}
                                style={{
                                    maxWidth: "100%",
                                    maxHeight: "100%",
                                    objectFit: "contain",
                                    display: "block",
                                }}
                            />
                        </div>
                    ))}
                </div>
            </div>

            {/* spacer that drives vertical->horizontal mapping */}
            <div ref={spacerRef} style={{ height: spacerHeight || 200 }} />

            {/* subsequent article content continues after the spacer */}
        </section>
    );
}
